import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Is this browser a command-and-control display?
 *
 * NO CREDENTIAL LIVES HERE. The enrolment token is held by the browser as an httpOnly,
 * path-scoped cookie that this code cannot read — which is the point, because that token is
 * long-lived by necessity: the screen has to re-authenticate itself after a power cut with
 * nobody present. A 90-day secret sitting in localStorage, readable by any script on a page
 * that renders dashboards, was the wrong trade for that.
 *
 * What IS persisted is the fact that this browser has been enrolled — one boolean and two
 * display labels. It survives a reload so the screen knows what it is before the network
 * answers, and it is reconciled against the server on every start: if the session call
 * fails, this is cleared.
 *
 * `isDisplay` GRANTS NOTHING. It suppresses the screen lock and the idle sign-out in this
 * tab. The server decides what a display session may do, from the credential presented, and
 * refuses every non-GET regardless of what the client believes about itself. Setting this by
 * hand in devtools buys an attacker a browser that stops locking their own screen.
 */
interface DisplayState {
  /** True once a display session has been established in this browser. */
  isDisplay: boolean
  name: string | null
  layout: Array<{ path: string; title?: string }>
  setSession: (opts: { name: string | null; layout: Array<{ path: string; title?: string }> }) => void
  clear: () => void
}

export const useDisplayStore = create<DisplayState>()(
  persist(
    (set) => ({
      isDisplay: false,
      name: null,
      layout: [],
      setSession: ({ name, layout }) => set({ isDisplay: true, name, layout }),
      clear: () => set({ isDisplay: false, name: null, layout: [] }),
    }),
    { name: 'vs-display' }
  )
)

/** Read outside React (the idle timer) without subscribing. */
export const isDisplaySession = () => useDisplayStore.getState().isDisplay
